import React, { useCallback, useId, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { notification, Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEllipsis, faPencil } from '@fortawesome/pro-solid-svg-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './organizational-structure.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { colorPalette } from 'shared/constants';
import { TextInput, Select, IconButton, Dropdown, SearchBox } from 'src/components/design-system';
import { Modal } from 'src/components/design-system/modal';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { getLabelsList } from 'shared/store/selectors/labels.selector';
import { createLabel, updateLabel } from 'shared/store/actions/labels.actions';

const ALL_COLORS = [
  '#687ECA',
  '#DE6969',
  '#729E39',
  '#EF9327',
  '#565656',
  '#867AB6',
  '#AB7151',
  '#067011',
  '#8B1818',
  '#CC45C7',
  '#4F3B30',
  '#309BA9',
  '#FFC107',
  '#3B7BFA',
];

/**
 * Location card — composed into the Organizational group from the existing
 * `organizationLabels` collection (not from `/profile-fields`). Locations carry
 * a color, so the add/edit modal differs from the profile-field cards. Supports
 * add / edit (labels API is unchanged; no delete).
 */
export function LocationCard() {
  const dispatch = useDispatch();
  const lang = useSelector(getLang('SETTINGS'));
  const headingId = useId();
  const labels = useSelector(getLabelsList);
  const [api, contextHolder] = notification.useNotification();

  const [filter, setFilter] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedLabelCode, setSelectedLabelCode] = useState(null);

  const selectedLabel = useMemo(
    () => labels.find(l => l.code === selectedLabelCode),
    [labels, selectedLabelCode],
  );

  const visibleLabels = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return labels;
    return labels.filter(l => (l.name || '').toLowerCase().includes(q));
  }, [labels, filter]);

  const usedColors = useMemo(() => labels.map(l => l.color), [labels]);

  const availableColorOptions = useMemo(
    () =>
      ALL_COLORS.toSorted((a, b) => {
        const aUsed = usedColors.includes(a) && a !== selectedLabel?.color;
        const bUsed = usedColors.includes(b) && b !== selectedLabel?.color;
        return Number(aUsed) - Number(bUsed);
      }).map(color => ({ value: color, label: color })),
    [usedColors, selectedLabel],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().trim().required(lang.FIELD_REQUIRED),
        code: Yup.string()
          .trim()
          .required(lang.FIELD_REQUIRED)
          .max(4, lang.MAX_4_CHARS)
          .test('unique-code', lang.CODE_MUST_BE_UNIQUE, value => {
            if (!value) return true;
            return !labels.some(
              l => l.code === value && (!selectedLabel || l.code !== selectedLabel.code),
            );
          }),
        color: Yup.string().when('code', {
          is: val => !!val,
          then: schema => schema.required(lang.FIELD_REQUIRED),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    [labels, selectedLabel, lang],
  );

  const onSubmit = async () => {
    setEditLoading(true);
    try {
      const payload = { name: values.name, code: values.code, color: values.color };
      const result = selectedLabel
        ? await dispatch(updateLabel(payload))
        : await dispatch(createLabel(payload));

      if (result?.error) {
        api.error({ message: lang.LOCATION_ERROR, placement: 'bottomRight' });
        return;
      }
      api.success({
        message: selectedLabel ? lang.LOCATION_UPDATED : lang.LOCATION_CREATED,
        placement: 'bottomRight',
      });
      closeModal();
    } catch (error) {
      api.error({ message: lang.LOCATION_ERROR, placement: 'bottomRight' });
    } finally {
      setEditLoading(false);
    }
  };

  const { handleSubmit, setFieldValue, resetForm, values, errors, isValid } = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      name: selectedLabel?.name || '',
      code: selectedLabel?.code || '',
      color: selectedLabel?.color || '',
    },
    onSubmit,
  });

  const closeModal = () => {
    setSelectedLabelCode(null);
    setEditModalOpen(false);
    resetForm();
    setEditLoading(false);
  };

  const handleEdit = useCallback(label => {
    setSelectedLabelCode(label.code);
    setEditModalOpen(true);
  }, []);

  const colorOptionRender = option => (
    <span className={styles.colorTag} style={{ backgroundColor: option.data.value }}>
      {values.code || values.name || option.data.value}
    </span>
  );

  const colorLabelRender = ({ value }) => (
    <span className={styles.colorTag} style={{ backgroundColor: value }}>
      {values.code || values.name || value}
    </span>
  );

  const renderBody = () => {
    if (visibleLabels.length === 0) {
      return (
        <div className={styles.emptyHint}>{filter.trim() ? lang.NO_MATCHES : lang.NO_OPTIONS}</div>
      );
    }
    return (
      <ListItemsNavigator className={styles.optionList} aria-labelledby={headingId}>
        {visibleLabels.map(label => {
          const menuItems = [
            {
              key: 'edit',
              icon: <FontAwesomeIcon icon={faPencil} color={colorPalette.primary} />,
              label: <span style={{ color: colorPalette.primary }}>{lang.MENU_EDIT}</span>,
              onClick: () => handleEdit(label),
            },
          ];
          return (
            <div key={label.code} className={styles.optionRow}>
              <span className={styles.optionText}>
                <span className={styles.colorDot} style={{ backgroundColor: label.color }} />
                {label.name}
              </span>
              <Dropdown
                items={menuItems}
                trigger={['click']}
                placement="bottomRight"
                aria-label={lang.OPTION_ACTIONS}
                childrenWrapperStyle={styles.kebab}>
                <FontAwesomeIcon icon={faEllipsis} color={colorPalette.primaryMain6} />
              </Dropdown>
            </div>
          );
        })}
      </ListItemsNavigator>
    );
  };

  return (
    // See FieldCard: role="group" + aria-label gives the card a unique
    // accessible container so its shared-aria-label controls can be scoped.
    <div className={styles.card} role="group" aria-label={lang.LOCATION_CARD_TITLE}>
      {contextHolder}
      <div className={styles.cardHeader}>
        <h3 id={headingId} className={styles.cardTitle}>
          {lang.LOCATION_CARD_TITLE}
          <span aria-hidden="true">{` (${formatNumber(labels.length)})`}</span>
        </h3>
        <IconButton
          label={lang.ADD_LOCATION}
          className={styles.addButton}
          onClick={() => setEditModalOpen(true)}>
          <FontAwesomeIcon icon={faPlus} />
        </IconButton>
      </div>

      <SearchBox
        className={styles.searchBox}
        placeholder={lang.FILTER_PLACEHOLDER}
        value={filter}
        onChange={e => setFilter(e.target.value)}
        aria-label={lang.FILTER_PLACEHOLDER}
      />

      {renderBody()}

      <Modal
        title={selectedLabel ? lang.EDIT_LOCATION : lang.ADD_LOCATION}
        open={editModalOpen}
        onCancel={closeModal}
        onSubmit={handleSubmit}
        cancelText={lang.CANCEL}
        submitText={selectedLabel ? lang.EDIT_LOCATION : lang.ADD_LOCATION}
        submitDisabled={
          !isValid ||
          !values.name.trim() ||
          !values.color ||
          (!selectedLabel && !values.code.trim())
        }
        loading={editLoading}
        footerAlign="end"
        className={styles.modal}>
        {selectedLabel && (
          <Alert
            type="info"
            showIcon
            message={lang.EDIT_LOCATION_DISCLAIMER}
            className={styles.disclaimer}
          />
        )}
        <TextInput
          label={lang.LOCATION_NAME}
          name="name"
          value={values.name}
          onChange={e => setFieldValue('name', e.target.value)}
          error={errors.name}
          size="middle"
        />
        {!selectedLabel && (
          <TextInput
            label={lang.LOCATION_CODE}
            name="code"
            value={values.code}
            onChange={e => setFieldValue('code', e.target.value.toUpperCase())}
            error={errors.code}
            maxLength={4}
            size="middle"
          />
        )}
        <Select
          label={lang.LOCATION_COLOR}
          name="color"
          value={values.color}
          onChange={setFieldValue}
          options={availableColorOptions}
          error={errors.color}
          size="middle"
          optionRender={colorOptionRender}
          labelRender={colorLabelRender}
          showSearch={false}
          allowClear={false}
          disabled={!selectedLabel && !values.code}
        />
      </Modal>
    </div>
  );
}
